/**
 * BillingTab — List invoices, create invoice, record payments, per-tenant filter.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Receipt, DollarSign, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { superAdminApi } from './superAdminApi';

const STATUS_COLORS = {
  paid: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
  pending: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
  partial: 'bg-sky-500/20 text-sky-300 border-sky-400/30',
  overdue: 'bg-rose-500/20 text-rose-300 border-rose-400/30',
  cancelled: 'bg-slate-500/20 text-slate-400 border-slate-400/30',
};

export const BillingTab = () => {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);

  const params = {};
  if (statusFilter !== 'all') params.status = statusFilter;

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['superadmin-invoices', statusFilter],
    queryFn: () => superAdminApi.listInvoices(params),
  });

  const { data: tenants = [] } = useQuery({
    queryKey: ['superadmin-tenants', {}],
    queryFn: () => superAdminApi.listTenants({ limit: 500 }),
  });

  return (
    <div className="space-y-4" data-testid="superadmin-billing-tab">
      <div className="flex flex-wrap items-center gap-2">
        {['all', 'pending', 'partial', 'paid', 'overdue'].map(s => (
          <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(s)}
                  className={statusFilter === s ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'text-white border-white/20 bg-white/5 hover:bg-white/10'}
                  data-testid={`invoice-filter-${s}`}>
            <span className="capitalize">{s}</span>
          </Button>
        ))}
        <Button onClick={() => setCreateOpen(true)} size="sm"
                className="ml-auto bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="create-invoice-btn">
          <Plus className="h-4 w-4 mr-1" /> New Invoice
        </Button>
      </div>

      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left p-3">Invoice #</th>
                  <th className="text-left p-3">Tenant</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-right p-3">Paid</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-right p-3 pr-5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading...</td></tr>}
                {!isLoading && invoices.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">No invoices found.</td></tr>}
                {invoices.map(inv => (
                  <tr key={inv.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 font-mono text-xs">{inv.number}</td>
                    <td className="p-3">
                      <p className="font-semibold">{inv.tenant_name || '—'}</p>
                      <p className="text-xs text-slate-500 font-mono">{inv.tenant_code}</p>
                    </td>
                    <td className="p-3 text-right font-mono">${inv.amount} {inv.currency}</td>
                    <td className="p-3 text-right font-mono text-emerald-300">${inv.amount_paid || 0}</td>
                    <td className="p-3">
                      <Badge variant="outline" className={`capitalize ${STATUS_COLORS[inv.status] || ''}`}>{inv.status}</Badge>
                    </td>
                    <td className="p-3 text-xs text-slate-400">
                      {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-3 text-right pr-5">
                      {inv.status !== 'paid' && (
                        <Button size="sm" onClick={() => setPayFor(inv)}
                                className="bg-emerald-500 hover:bg-emerald-400 text-white"
                                data-testid={`pay-invoice-${inv.id}`}>
                          <DollarSign className="h-3 w-3 mr-1" /> Record Payment
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <CreateInvoiceDialog open={createOpen} onOpenChange={setCreateOpen}
                           tenants={tenants}
                           onCreated={() => {
                             qc.invalidateQueries({ queryKey: ['superadmin-invoices'] });
                             qc.invalidateQueries({ queryKey: ['superadmin-dashboard'] });
                           }} />
      {payFor && <RecordPaymentDialog invoice={payFor} onClose={() => setPayFor(null)}
                                      onPaid={() => {
                                        qc.invalidateQueries({ queryKey: ['superadmin-invoices'] });
                                        qc.invalidateQueries({ queryKey: ['superadmin-dashboard'] });
                                      }} />}
    </div>
  );
};

const CreateInvoiceDialog = ({ open, onOpenChange, tenants, onCreated }) => {
  const [form, setForm] = useState({ tenant_id: '', amount: '', currency: 'USD', description: '' });
  const mut = useMutation({
    mutationFn: (data) => superAdminApi.createInvoice({ ...data, amount: parseFloat(data.amount) || 0 }),
    onSuccess: (r) => {
      toast.success(`Invoice ${r.number} created`);
      onOpenChange(false);
      onCreated?.();
      setForm({ tenant_id: '', amount: '', currency: 'USD', description: '' });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 text-white border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> New Invoice</DialogTitle>
          <DialogDescription className="text-slate-400">Manual invoice for a tenant</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Tenant *</Label>
            <Select value={form.tenant_id} onValueChange={v => setForm({ ...form, tenant_id: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="invoice-tenant-select">
                <SelectValue placeholder="Select tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants.map(t => <SelectItem key={t.tenant_id || t.id} value={t.tenant_id || t.id}>{t.name} ({t.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Amount *</Label>
              <Input type="number" step="0.01" value={form.amount}
                     onChange={e => setForm({ ...form, amount: e.target.value })}
                     className="bg-white/5 border-white/10 text-white" data-testid="invoice-amount" />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={v => setForm({ ...form, currency: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['USD', 'EUR', 'GBP', 'ZAR', 'ZWL'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                   placeholder="e.g. Basic Plan - March 2026"
                   className="bg-white/5 border-white/10 text-white" data-testid="invoice-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="text-white border-white/20 bg-white/5">Cancel</Button>
          <Button onClick={() => mut.mutate(form)}
                  disabled={mut.isPending || !form.tenant_id || !form.amount}
                  className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="invoice-submit-btn">
            {mut.isPending ? 'Creating...' : 'Create Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RecordPaymentDialog = ({ invoice, onClose, onPaid }) => {
  const remaining = Math.max(0, (invoice.amount || 0) - (invoice.amount_paid || 0));
  const [form, setForm] = useState({
    amount: remaining, method: 'manual', reference: '', note: '',
  });
  const mut = useMutation({
    mutationFn: (data) => superAdminApi.recordPayment({ invoice_id: invoice.id, ...data, amount: parseFloat(data.amount) || 0 }),
    onSuccess: (r) => {
      toast.success(`Payment recorded — Invoice ${r.invoice_status}`);
      onClose();
      onPaid?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 text-white border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" /> Record Payment</DialogTitle>
          <DialogDescription className="text-slate-400">
            Invoice {invoice.number} · {invoice.tenant_name} · Remaining: ${remaining}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Amount *</Label>
            <Input type="number" step="0.01" value={form.amount}
                   onChange={e => setForm({ ...form, amount: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" data-testid="payment-amount" />
          </div>
          <div>
            <Label>Method</Label>
            <Select value={form.method} onValueChange={v => setForm({ ...form, method: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['manual', 'bank_transfer', 'stripe', 'ecocash', 'paypal', 'cash'].map(m => (
                  <SelectItem key={m} value={m} className="capitalize">{m.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reference</Label>
            <Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })}
                   placeholder="Transaction ID, cheque #, etc." className="bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <Label>Note</Label>
            <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="text-white border-white/20 bg-white/5">Cancel</Button>
          <Button onClick={() => mut.mutate(form)}
                  disabled={mut.isPending || !form.amount}
                  className="bg-emerald-500 hover:bg-emerald-400 text-white" data-testid="payment-submit-btn">
            {mut.isPending ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
